import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SearchIcon, UserMinusIcon, AlertTriangleIcon, CheckIcon, XIcon, Loader2Icon, ShieldIcon, DatabaseIcon, UsersIcon, SettingsIcon, ArrowRightIcon, FileTextIcon, BriefcaseIcon } from 'lucide-react';
interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  title: string;
  profile: string;
  role: string;
  status: string;
  subordinates: string[];
  hasNMLS: boolean;
}
const MOCK_USERS: User[] = [{
  id: '1',
  firstName: 'John',
  lastName: 'Martinez',
  email: 'john.martinez@activelinkmortgage.com',
  title: 'Loan Officer',
  profile: 'Loan Associate',
  role: 'Jr. Loan Officer',
  status: 'Active',
  subordinates: [],
  hasNMLS: true
}, {
  id: '2',
  firstName: 'Sarah',
  lastName: 'Chen',
  email: 'sarah.chen@activelinkmortgage.com',
  title: 'Jr Processor',
  profile: 'Loan Processor',
  role: 'Processor',
  status: 'Active',
  subordinates: [],
  hasNMLS: false
}, {
  id: '3',
  firstName: 'Mike',
  lastName: 'Johnson',
  email: 'mike.johnson@activelinkmortgage.com',
  title: 'Team Lead',
  profile: 'Team Lead',
  role: 'Team Lead',
  status: 'Active',
  subordinates: ['1', '5'],
  hasNMLS: true
}, {
  id: '4',
  firstName: 'Emily',
  lastName: 'Davis',
  email: 'emily.davis@activelinkmortgage.com',
  title: 'Division Lead',
  profile: 'Division Lead',
  role: 'Division Lead',
  status: 'Active',
  subordinates: ['3', '7'],
  hasNMLS: true
}, {
  id: '5',
  firstName: 'David',
  lastName: 'Wilson',
  email: 'david.wilson@activelinkmortgage.com',
  title: 'Loan Associate',
  profile: 'Loan Associate',
  role: 'Solution Desk',
  status: 'Active',
  subordinates: [],
  hasNMLS: false
}, {
  id: '6',
  firstName: 'Lisa',
  lastName: 'Thompson',
  email: 'lisa.thompson@activelinkmortgage.com',
  title: 'Sr Processor',
  profile: 'Processing Director',
  role: 'Processor Manager',
  status: 'Active',
  subordinates: ['2', '8'],
  hasNMLS: false
}, {
  id: '7',
  firstName: 'James',
  lastName: 'Brown',
  email: 'james.brown@activelinkmortgage.com',
  title: 'Team Lead',
  profile: 'Team Lead',
  role: 'Team Lead',
  status: 'Active',
  subordinates: ['9', '10'],
  hasNMLS: true
}, {
  id: '8',
  firstName: 'Amanda',
  lastName: 'Garcia',
  email: 'amanda.garcia@activelinkmortgage.com',
  title: 'Doc Specialist',
  profile: 'Loan Processor',
  role: 'Processor',
  status: 'Active',
  subordinates: [],
  hasNMLS: false
}, {
  id: '9',
  firstName: 'Robert',
  lastName: 'Taylor',
  email: 'robert.taylor@activelinkmortgage.com',
  title: 'Loan Officer',
  profile: 'Loan Associate',
  role: 'Jr. Loan Officer',
  status: 'Active',
  subordinates: [],
  hasNMLS: true
}, {
  id: '10',
  firstName: 'Jennifer',
  lastName: 'Lee',
  email: 'jennifer.lee@activelinkmortgage.com',
  title: 'Loan Officer',
  profile: 'Loan Associate',
  role: 'Jr. Loan Officer',
  status: 'Active',
  subordinates: [],
  hasNMLS: true
}];
interface OffboardingConfig {
  title: string;
  removePermissions: string[];
  revokeLicenses: string[];
  removePublicGroups: string[];
  requiresRoundRobinDeactivation: boolean;
  requiresOwnershipTransfer: boolean;
  createRoundRobinOnExit: boolean;
  requiresNMLSHandling: boolean;
}
const MOCK_METADATA: Record<string, OffboardingConfig> = {
  'Loan Officer': {
    title: 'Loan Officer',
    removePermissions: ['Sales User', 'Encompass Sync', 'Dialer Access'],
    revokeLicenses: ['Salesforce', 'Pulse', 'Dialer'],
    removePublicGroups: ['All Sales', 'West Coast Sales'],
    requiresRoundRobinDeactivation: true,
    requiresOwnershipTransfer: true,
    createRoundRobinOnExit: false,
    requiresNMLSHandling: true
  },
  'Jr Processor': {
    title: 'Jr Processor',
    removePermissions: ['Processor Base', 'DocuSign Sender'],
    revokeLicenses: ['Salesforce', 'Pulse'],
    removePublicGroups: ['All Processors'],
    requiresRoundRobinDeactivation: true,
    requiresOwnershipTransfer: true,
    createRoundRobinOnExit: true,
    requiresNMLSHandling: false
  },
  'Sr Processor': {
    title: 'Sr Processor',
    removePermissions: ['Processor Admin', 'DocuSign Sender', 'Encompass Admin'],
    revokeLicenses: ['Salesforce', 'Pulse'],
    removePublicGroups: ['All Processors', 'Processor Managers'],
    requiresRoundRobinDeactivation: true,
    requiresOwnershipTransfer: true,
    createRoundRobinOnExit: false,
    requiresNMLSHandling: false
  },
  'Team Lead': {
    title: 'Team Lead',
    removePermissions: ['Sales Manager', 'Reporting Admin', 'Dialer Access'],
    revokeLicenses: ['Salesforce', 'Pulse', 'Dialer'],
    removePublicGroups: ['All Sales', 'Team Leads'],
    requiresRoundRobinDeactivation: true,
    requiresOwnershipTransfer: true,
    createRoundRobinOnExit: false,
    requiresNMLSHandling: true
  },
  'Division Lead': {
    title: 'Division Lead',
    removePermissions: ['Division Admin', 'Reporting Admin', 'Dialer Access'],
    revokeLicenses: ['Salesforce', 'Pulse', 'Dialer'],
    removePublicGroups: ['All Sales', 'Division Leads', 'Executive'],
    requiresRoundRobinDeactivation: false,
    requiresOwnershipTransfer: true,
    createRoundRobinOnExit: false,
    requiresNMLSHandling: true
  },
  'Doc Specialist': {
    title: 'Doc Specialist',
    removePermissions: ['Doc Specialist Base'],
    revokeLicenses: ['Salesforce'],
    removePublicGroups: ['All Processors'],
    requiresRoundRobinDeactivation: false,
    requiresOwnershipTransfer: true,
    createRoundRobinOnExit: true,
    requiresNMLSHandling: false
  },
  'Loan Associate': {
    title: 'Loan Associate',
    removePermissions: ['Sales User', 'Dialer Access'],
    revokeLicenses: ['Salesforce', 'Pulse'],
    removePublicGroups: ['All Sales'],
    requiresRoundRobinDeactivation: true,
    requiresOwnershipTransfer: true,
    createRoundRobinOnExit: false,
    requiresNMLSHandling: false
  }
};
const DEFAULT_CONFIG: OffboardingConfig = {
  title: 'Default',
  removePermissions: ['Standard User'],
  revokeLicenses: ['Salesforce'],
  removePublicGroups: ['All Users'],
  requiresRoundRobinDeactivation: false,
  requiresOwnershipTransfer: true,
  createRoundRobinOnExit: false,
  requiresNMLSHandling: false
};
const AVATAR_COLORS = ['bg-blue-100 text-blue-700', 'bg-green-100 text-green-700', 'bg-purple-100 text-purple-700', 'bg-amber-100 text-amber-700', 'bg-pink-100 text-pink-700', 'bg-indigo-100 text-indigo-700'];
const getAvatarColor = (name: string) => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};
interface OffboardingPanelProps {
  onStepChange?: (step: number) => void;
}
export function OffboardingPanel({
  onStepChange
}: OffboardingPanelProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [reassignments, setReassignments] = useState<Record<string, string>>({});
  const [transferLeads, setTransferLeads] = useState<string>('');
  const [transferLoanOfficer, setTransferLoanOfficer] = useState<string>('');
  const [transferLOA, setTransferLOA] = useState<string>('');
  const [transferLOA2, setTransferLOA2] = useState<string>('');
  const [transferLoanProcessor, setTransferLoanProcessor] = useState<string>('');
  const [transferProcessor2, setTransferProcessor2] = useState<string>('');
  const [transferCases, setTransferCases] = useState<string>('');
  const [separationReason, setSeparationReason] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [deactivatedUsers, setDeactivatedUsers] = useState<Set<string>>(new Set());
  useEffect(() => {
    onStepChange?.(currentStep);
  }, [currentStep, onStepChange]);
  const activeUsers = useMemo(() => {
    return MOCK_USERS.filter(u => !deactivatedUsers.has(u.id));
  }, [deactivatedUsers]);
  const filteredUsers = useMemo(() => {
    const query = searchQuery.toLowerCase();
    return activeUsers.filter(user => user.firstName.toLowerCase().includes(query) || user.lastName.toLowerCase().includes(query) || user.email.toLowerCase().includes(query) || user.title.toLowerCase().includes(query));
  }, [searchQuery, activeUsers]);
  const selectedUser = useMemo(() => {
    return activeUsers.find(u => u.id === selectedUserId) || null;
  }, [selectedUserId, activeUsers]);
  const config = useMemo(() => {
    if (!selectedUser) return null;
    return MOCK_METADATA[selectedUser.title] || DEFAULT_CONFIG;
  }, [selectedUser]);
  const subordinates = useMemo(() => {
    if (!selectedUser) return [];
    return activeUsers.filter(u => selectedUser.subordinates.includes(u.id));
  }, [selectedUser, activeUsers]);
  const isLoanOfficerType = useMemo(() => {
    if (!selectedUser) return false;
    return ['Loan Officer', 'Loan Associate', 'Team Lead', 'Division Lead'].includes(selectedUser.title);
  }, [selectedUser]);
  const isProcessorType = useMemo(() => {
    if (!selectedUser) return false;
    return ['Jr Processor', 'Sr Processor', 'Doc Specialist'].includes(selectedUser.title);
  }, [selectedUser]);
  const handleNext = () => {
    if (currentStep === 1 && subordinates.length === 0) {
      // Skip reassignment if no subordinates
      setCurrentStep(3);
    } else if (currentStep < 4) {
      setCurrentStep(prev => prev + 1);
    }
  };
  const handleBack = () => {
    if (currentStep === 3 && subordinates.length === 0) {
      setCurrentStep(1);
    } else if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };
  const executeOffboarding = () => {
    setIsProcessing(true);
    setTimeout(() => {
      setIsProcessing(false);
      if (selectedUserId) {
        setDeactivatedUsers(prev => new Set(prev).add(selectedUserId));
      }
      setCurrentStep(4);
    }, 2000);
  };
  const resetFlow = () => {
    setSelectedUserId(null);
    setSearchQuery('');
    setReassignments({});
    setTransferLeads('');
    setTransferLoanOfficer('');
    setTransferLOA('');
    setTransferLOA2('');
    setTransferLoanProcessor('');
    setTransferProcessor2('');
    setTransferCases('');
    setSeparationReason('');
    setCurrentStep(0);
  };
  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        // Select User
        return <div className="flex flex-col h-full" data-id="element-22" data-mp="um0fdem016nj">
            <div className="mb-6" data-id="element-23" data-mp="umsaypflj4dt">
              <h2 className="text-xl font-bold text-gray-900 mb-2" data-id="element-24" data-mp="qkon11bqq7bn">
                Identify User
              </h2>
              <p className="text-sm text-gray-500" data-id="element-25" data-mp="gh017ec7mfw0">
                Search and select the user to terminate. This will initiate the
                metadata-driven offboarding process.
              </p>
            </div>

            <div className="relative mb-6" data-id="element-26" data-mp="6pvqiqtkhh6z">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-27" data-mp="6oudwhwwkmn5">
                <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-28" data-mp="bnourebb814m" />
              </div>
              <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-navy-600 focus:border-navy-600 sm:text-sm transition-colors" placeholder="Search users by name, email, or title..." data-id="element-29" data-mp="3swu0fq0oaopx" />
            </div>

            <div className="flex-1 overflow-y-auto pb-6" data-id="element-30" data-mp="1n4onuc5hejzp">
              {filteredUsers.length === 0 ? <div className="text-center py-12" data-id="element-31" data-mp="1kruw92uvgq3e">
                  <p className="text-sm text-gray-500" data-id="element-32" data-mp="1goewvyrf4bn0">
                    No active users found matching "{searchQuery}"
                  </p>
                </div> : <div className="space-y-2" data-id="element-33" data-mp="16r0kqbcj290i">
                  {filteredUsers.map(user => {
                const isSelected = selectedUserId === user.id;
                return <div key={user.id} onClick={() => setSelectedUserId(user.id)} className={`flex items-center p-4 rounded-xl border cursor-pointer transition-colors ${isSelected ? 'border-navy-600 bg-navy-50 shadow-md ring-1 ring-navy-600/20' : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 shadow-sm'}`} data-id="element-34" data-mp="1zfg04yzkmtcd">
                        <div className="flex-shrink-0 mr-4" data-id="element-35" data-mp="2ie0io2necqee">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold ${getAvatarColor(user.firstName)}`} data-id="element-36" data-mp="2ich6oo8rx1if">
                            {user.firstName[0]}
                            {user.lastName[0]}
                          </div>
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-37" data-mp="34oac6y60i7ff">
                          <div className="flex items-center justify-between mb-1" data-id="element-38" data-mp="38ou9q2uhzd23">
                            <p className="text-base font-bold text-gray-900 truncate" data-id="element-39" data-mp="3dnduhzxcuisv">
                              {user.firstName} {user.lastName}
                            </p>
                            {user.subordinates.length > 0 && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-800" data-id="element-40" data-mp="3nbvvah5mwpwi">
                                <UsersIcon className="w-3 h-3 mr-1" data-id="element-41" data-mp="87vfcj6qmf1" />
                                Manages {user.subordinates.length}
                              </span>}
                          </div>
                          <div className="flex items-center text-sm text-gray-500" data-id="element-42" data-mp="sx7zc2owcd5j">
                            <span className="font-medium text-gray-700 mr-3" data-id="element-43" data-mp="16pxkk4wlpe1l">
                              {user.title}
                            </span>
                            <span className="truncate" data-id="element-44" data-mp="12q6vc5mq35z6">{user.email}</span>
                          </div>
                        </div>
                      </div>;
              })}
                </div>}
            </div>
          </div>;
      case 1:
        // Review Config
        if (!selectedUser || !config) return null;
        return <div className="flex flex-col h-full" data-id="element-45" data-mp="iys6nva80vtx">
            <div className="mb-6" data-id="element-46" data-mp="1zdtonuoi288e">
              <h2 className="text-xl font-bold text-gray-900 mb-2" data-id="element-47" data-mp="1zcs1iru7lvab">
                Review Configuration
              </h2>
              <p className="text-sm text-gray-500" data-id="element-48" data-mp="1uhcqe7nl6dhl">
                Loaded{' '}
                <code className="bg-gray-100 px-1 py-0.5 rounded text-xs" data-id="element-49" data-mp="22upmgivkvesr">
                  User_Offboarding_Config__mdt
                </code>{' '}
                for title:{' '}
                <span className="font-bold text-gray-700" data-id="element-50" data-mp="1szeecjeod5tz">
                  {selectedUser.title}
                </span>
              </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden mb-6" data-id="element-51" data-mp="2lms7e6f95h8t">
              <div className="p-4 border-b border-gray-100 bg-gray-50 flex items-center" data-id="element-52" data-mp="2qllhrpz8ss4r">
                <SettingsIcon className="w-5 h-5 text-gray-500 mr-2" data-id="element-53" data-mp="2bwysavfwijzt" />
                <h3 className="text-sm font-bold text-gray-900" data-id="element-54" data-mp="2bwp3vcai8kw8">
                  Metadata Resolution
                </h3>
              </div>
              <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-55" data-mp="34l4fdoufgl2t">
                <div data-id="element-56" data-mp="34jtdjukjlnec">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3" data-id="element-57" data-mp="39cutm5whjszi">
                    Permissions to Remove
                  </h4>
                  <ul className="space-y-2" data-id="element-58" data-mp="3j5z1qyjngafo">
                    {config.removePermissions.map(p => <li key={p} className="flex items-center text-sm text-gray-700" data-id="element-59" data-mp="3e7fb4ku42agz">
                        <XIcon className="w-4 h-4 text-red-500 mr-2" data-id="element-60" data-mp="357seeccbtsu9" /> {p}
                      </li>)}
                  </ul>
                </div>
                <div data-id="element-61" data-mp="2vi8qgs9rb8sj">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3" data-id="element-62" data-mp="2vhpia39wryrp">
                    Licenses to Revoke
                  </h4>
                  <ul className="space-y-2" data-id="element-63" data-mp="2lkumhs50sch4">
                    {config.revokeLicenses.map(l => <li key={l} className="flex items-center text-sm text-gray-700" data-id="element-64" data-mp="2hipt8uihhhv0">
                        <XIcon className="w-4 h-4 text-red-500 mr-2" data-id="element-65" data-mp="2ckoaybsvtmkl" /> {l}
                      </li>)}
                  </ul>
                </div>
              </div>
              <div className="p-4 border-t border-gray-100 bg-gray-50 grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-66" data-mp="wzdr8b9qe111">
                <div className="flex items-center justify-between" data-id="element-67" data-mp="113bn8d3revpv">
                  <span className="text-sm text-gray-600" data-id="element-68" data-mp="160kags3rlpxz">
                    Round Robin Deactivation
                  </span>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${config.requiresRoundRobinDeactivation ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-600'}`} data-id="element-69" data-mp="9d78atyrr0w1">
                    {config.requiresRoundRobinDeactivation ? 'Required' : 'Skip'}
                  </span>
                </div>
                <div className="flex items-center justify-between" data-id="element-70" data-mp="3ls81y800eq9e">
                  <span className="text-sm text-gray-600" data-id="element-71" data-mp="3qr2onl13kyys">
                    Ownership Transfer
                  </span>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${config.requiresOwnershipTransfer ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-600'}`} data-id="element-72" data-mp="2k51vg1kni48n">
                    {config.requiresOwnershipTransfer ? 'Required' : 'Skip'}
                  </span>
                </div>
              </div>
            </div>

            {/* Special Logic Alerts */}
            <div className="space-y-3" data-id="element-73" data-mp="331jgy18wp7wg">
              {config.createRoundRobinOnExit && <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start" data-id="element-74" data-mp="2y4196e6mkyq1">
                  <AlertTriangleIcon className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" data-id="element-75" data-mp="3cse5jc9mkjl2" />
                  <div data-id="element-76" data-mp="37uw6cdletiq9">
                    <h4 className="text-sm font-bold text-blue-900" data-id="element-77" data-mp="3ltvk2q7dwd7n">
                      Processor Special Logic
                    </h4>
                    <p className="text-xs text-blue-700 mt-1" data-id="element-78" data-mp="3gvu268lp2jxr">
                      User is a Processor (Not Director). System will
                      automatically create a Round Robin entry upon deactivation
                      to redistribute active files.
                    </p>
                  </div>
                </div>}
              {config.requiresNMLSHandling && selectedUser.hasNMLS && <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 flex items-start" data-id="element-79" data-mp="3vfwtq9p58fle">
                  <ShieldIcon className="w-5 h-5 text-purple-600 mt-0.5 mr-3 flex-shrink-0" data-id="element-80" data-mp="3hos0bpcdcjji" />
                  <div data-id="element-81" data-mp="3mie2uootj71l">
                    <h4 className="text-sm font-bold text-purple-900" data-id="element-82" data-mp="3mimi2n8eyofy">
                      NMLS State License Handling
                    </h4>
                    <p className="text-xs text-purple-700 mt-1" data-id="element-83" data-mp="3cq8ahdunmnlv">
                      User has active NMLS requirements. State license
                      assignments will be archived and removed from active
                      routing pools.
                    </p>
                  </div>
                </div>}
            </div>
          </div>;
      case 2:
        // Reassignment
        if (!selectedUser) return null;
        return <div className="flex flex-col h-full" data-id="element-84" data-mp="2swql9gza7fdh">
            <div className="mb-6" data-id="element-85" data-mp="2sy1v9yjbljd1">
              <h2 className="text-xl font-bold text-gray-900 mb-2" data-id="element-86" data-mp="2sz3behf70w1m">
                Reassign Subordinates
              </h2>
              <p className="text-sm text-gray-500" data-id="element-87" data-mp="3lkw4fzaoswb7">
                <span className="font-bold text-gray-700" data-id="element-88" data-mp="3lm7hyqtxpifi">
                  {selectedUser.firstName} {selectedUser.lastName}
                </span>{' '}
                manages {subordinates.length} users. You must reassign them
                before termination.
              </p>
            </div>

            <div className="space-y-4" data-id="element-89" data-mp="3gsuysw80y3u5">
              {subordinates.map(sub => <div key={sub.id} className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-90" data-mp="8db8a4yqmzis">
                  <div className="flex items-center" data-id="element-91" data-mp="mibgloluiw2e">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold mr-3 ${getAvatarColor(sub.firstName)}`} data-id="element-92" data-mp="ihrmktwkk8kh">
                      {sub.firstName[0]}
                      {sub.lastName[0]}
                    </div>
                    <div data-id="element-93" data-mp="3mldfy26ftmx">
                      <p className="text-sm font-bold text-gray-900" data-id="element-94" data-mp="3ltsdh67heaw">
                        {sub.firstName} {sub.lastName}
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-95" data-mp="dk9c2z2wllad">{sub.title}</p>
                    </div>
                  </div>
                  <ArrowRightIcon className="w-5 h-5 text-gray-300 hidden md:block" data-id="element-96" data-mp="8jlo6m9z1kkd" />
                  <div className="flex-1 max-w-xs" data-id="element-97" data-mp="1oy3mazf1hyi6">
                    <select value={reassignments[sub.id] || ''} onChange={e => setReassignments({
                  ...reassignments,
                  [sub.id]: e.target.value
                })} className="block w-full pl-3 pr-10 py-2 text-sm border border-gray-300 focus:outline-none focus:ring-navy-600 focus:border-navy-600 rounded-md" data-id="element-98" data-mp="1p0ptuz1xke0e">
                      <option value="" data-id="element-99" data-mp="25xwoe9msgzqz">Select new manager...</option>
                      {activeUsers.filter(u => u.id !== selectedUser.id && u.id !== sub.id && (u.title.includes('Lead') || u.title.includes('Manager') || u.title.includes('Director'))).map(manager => <option key={manager.id} value={manager.id} data-id="element-100" data-mp="ukkgnnnwrwzj">
                            {manager.firstName} {manager.lastName} (
                            {manager.title})
                          </option>)}
                    </select>
                  </div>
                </div>)}
            </div>
          </div>;
      case 3:
        // Deactivation Actions
        if (!selectedUser || !config) return null;
        return <div className="flex flex-col h-full" data-id="element-101" data-mp="42gs4kc10vts">
            <div className="mb-6" data-id="element-102" data-mp="44a0c42afo3w">
              <h2 className="text-xl font-bold text-gray-900 mb-2" data-id="element-103" data-mp="44jp5obljp80">
                Deactivation Actions
              </h2>
              <p className="text-sm text-gray-500" data-id="element-104" data-mp="3qhs1pqqos6q0">
                Review the final actions to be executed. Ownership transfer is
                required.
              </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 mb-6" data-id="element-105" data-mp="3gjvtb6p4vpvl">
              <h3 className="text-sm font-bold text-gray-900 mb-4 border-b border-gray-100 pb-2" data-id="element-106" data-mp="3cisdulazzvuw">
                Ownership Transfer
              </h3>
              <p className="text-xs text-gray-500 mb-5" data-id="element-107" data-mp="54bwsnz3y8x4">
                Assign each record type to a specific user or distribute via
                Round Robin.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-108" data-mp="3sfthiryrc7i2">
                {/* Leads - always shown */}
                <div data-id="element-109" data-mp="bisg17jx2lk">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-110" data-mp="az9ch66085y">
                    Transfer Leads to
                  </label>
                  <select value={transferLeads} onChange={e => setTransferLeads(e.target.value)} className="block w-full pl-3 pr-10 py-2 text-sm border border-gray-300 focus:outline-none focus:ring-navy-600 focus:border-navy-600 rounded-md" data-id="element-111" data-mp="3njcrfs7zzj2c">
                    <option value="" data-id="element-112" data-mp="17oj48e8mcim8">Select target...</option>
                    <option value="round-robin" data-id="element-113" data-mp="1br7dgn18vrjq">🔄 Via Round Robin</option>
                    {activeUsers.filter(u => u.id !== selectedUser.id).map(u => <option key={u.id} value={u.id} data-id="element-114" data-mp="2sa96rlutm3v5">
                          {u.firstName} {u.lastName} ({u.title})
                        </option>)}
                  </select>
                </div>

                {/* Loan Officer role-specific fields */}
                {isLoanOfficerType && <>
                    <div data-id="element-115" data-mp="3azfsziutpdlp">
                      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-116" data-mp="3azz2qcabkvon">
                        Transfer Loans (Loan Officer) to
                      </label>
                      <select value={transferLoanOfficer} onChange={e => setTransferLoanOfficer(e.target.value)} className="block w-full pl-3 pr-10 py-2 text-sm border border-gray-300 focus:outline-none focus:ring-navy-600 focus:border-navy-600 rounded-md" data-id="element-117" data-mp="3jzlz2jo6zbau">
                        <option value="" data-id="element-118" data-mp="3pqf9c3ohqsnr">Select target...</option>
                        <option value="round-robin" data-id="element-119" data-mp="3unp5e26dy2kk">🔄 Via Round Robin</option>
                        {activeUsers.filter(u => u.id !== selectedUser.id).map(u => <option key={u.id} value={u.id} data-id="element-120" data-mp="7g4ntacx4kxv">
                              {u.firstName} {u.lastName} ({u.title})
                            </option>)}
                      </select>
                    </div>
                    <div data-id="element-121" data-mp="m8ewaa42vx3a">
                      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-122" data-mp="m7vmjgol0f0c">
                        Transfer Loans (LOA) to
                      </label>
                      <select value={transferLOA} onChange={e => setTransferLOA(e.target.value)} className="block w-full pl-3 pr-10 py-2 text-sm border border-gray-300 focus:outline-none focus:ring-navy-600 focus:border-navy-600 rounded-md" data-id="element-123" data-mp="v9mu6r2omvwu">
                        <option value="" data-id="element-124" data-mp="2bohhhpjxmpmw">Select target...</option>
                        <option value="round-robin" data-id="element-125" data-mp="2bp0xv1txp5yt">🔄 Via Round Robin</option>
                        {activeUsers.filter(u => u.id !== selectedUser.id).map(u => <option key={u.id} value={u.id} data-id="element-126" data-mp="33hhvx1gf5h7a">
                              {u.firstName} {u.lastName} ({u.title})
                            </option>)}
                      </select>
                    </div>
                    <div data-id="element-127" data-mp="2pn7gxbu851ef">
                      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-128" data-mp="2po0djjzgyaiu">
                        Transfer Loans (LOA 2) to
                      </label>
                      <select value={transferLOA2} onChange={e => setTransferLOA2(e.target.value)} className="block w-full pl-3 pr-10 py-2 text-sm border border-gray-300 focus:outline-none focus:ring-navy-600 focus:border-navy-600 rounded-md" data-id="element-129" data-mp="3ibd3830xeygd">
                        <option value="" data-id="element-130" data-mp="2lq0dhtl8k9iy">Select target...</option>
                        <option value="round-robin" data-id="element-131" data-mp="2lqjjqd1ix9h5">🔄 Via Round Robin</option>
                        {activeUsers.filter(u => u.id !== selectedUser.id).map(u => <option key={u.id} value={u.id} data-id="element-132" data-mp="30f6977kv7hm3">
                              {u.firstName} {u.lastName} ({u.title})
                            </option>)}
                      </select>
                    </div>
                  </>}

                {/* Processor role-specific fields */}
                {isProcessorType && <>
                    <div data-id="element-133" data-mp="1lhtlfw4lwc0m">
                      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-134" data-mp="1lhabp2p40txo">
                        Transfer Loans (Loan Processor) to
                      </label>
                      <select value={transferLoanProcessor} onChange={e => setTransferLoanProcessor(e.target.value)} className="block w-full pl-3 pr-10 py-2 text-sm border border-gray-300 focus:outline-none focus:ring-navy-600 focus:border-navy-600 rounded-md" data-id="element-135" data-mp="1vf81hummayse">
                        <option value="" data-id="element-136" data-mp="1ci6w48p5iwnx">Select target...</option>
                        <option value="round-robin" data-id="element-137" data-mp="17q5ahw63ftl8">🔄 Via Round Robin</option>
                        {activeUsers.filter(u => u.id !== selectedUser.id).map(u => <option key={u.id} value={u.id} data-id="element-138" data-mp="f0ya0rn2wtxc">
                              {u.firstName} {u.lastName} ({u.title})
                            </option>)}
                      </select>
                    </div>
                    <div data-id="element-139" data-mp="35vbs5ehdhs6e">
                      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-140" data-mp="30xk3qufxso6y">
                        Transfer Loans (Processor 2) to
                      </label>
                      <select value={transferProcessor2} onChange={e => setTransferProcessor2(e.target.value)} className="block w-full pl-3 pr-10 py-2 text-sm border border-gray-300 focus:outline-none focus:ring-navy-600 focus:border-navy-600 rounded-md" data-id="element-141" data-mp="3tnah319qvm20">
                        <option value="" data-id="element-142" data-mp="3erksqwh36wu4">Select target...</option>
                        <option value="round-robin" data-id="element-143" data-mp="28ah41yziy7f4">🔄 Via Round Robin</option>
                        {activeUsers.filter(u => u.id !== selectedUser.id).map(u => <option key={u.id} value={u.id} data-id="element-144" data-mp="iu3q9u7bxo9k">
                              {u.firstName} {u.lastName} ({u.title})
                            </option>)}
                      </select>
                    </div>
                  </>}

                {/* Cases - always shown */}
                <div data-id="element-145" data-mp="12g9v5rz3uf81">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-146" data-mp="12h1fg6n94qj0">
                    Transfer Cases to
                  </label>
                  <select value={transferCases} onChange={e => setTransferCases(e.target.value)} className="block w-full pl-3 pr-10 py-2 text-sm border border-gray-300 focus:outline-none focus:ring-navy-600 focus:border-navy-600 rounded-md" data-id="element-147" data-mp="1bgy96moi0fkx">
                    <option value="" data-id="element-148" data-mp="3i6mb2lvnx3hs">Select target...</option>
                    <option value="round-robin" data-id="element-149" data-mp="3i98txho075fb">🔄 Via Round Robin</option>
                    {activeUsers.filter(u => u.id !== selectedUser.id).map(u => <option key={u.id} value={u.id} data-id="element-150" data-mp="3k4w50aevozga">
                          {u.firstName} {u.lastName} ({u.title})
                        </option>)}
                  </select>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 mb-6" data-id="element-151" data-mp="3t411zcaskkqc">
              <h3 className="text-sm font-bold text-gray-900 mb-4 border-b border-gray-100 pb-2" data-id="element-152" data-mp="3p3h5may1ll59">
                Reason for Separation
              </h3>
              <div className="max-w-2xl" data-id="element-153" data-mp="1r6fpaf3154a">
                <select value={separationReason} onChange={e => setSeparationReason(e.target.value)} className="block w-full pl-3 pr-10 py-2 text-sm border border-gray-300 focus:outline-none focus:ring-navy-600 focus:border-navy-600 rounded-md mb-3" data-id="element-154" data-mp="1rg5oy04nc9z">
                  <option value="" data-id="element-155" data-mp="v8roztwskdpv">Select reason...</option>
                  <option value="voluntary-resignation" data-id="element-156" data-mp="zbfy82pf3mnd">
                    Voluntary Resignation
                  </option>
                  <option value="involuntary-termination" data-id="element-157" data-mp="lcgdv78xttx0">
                    Involuntary Termination
                  </option>
                  <option value="performance" data-id="element-158" data-mp="bmwtty9hnu0k">Performance Issues</option>
                  <option value="policy-violation" data-id="element-159" data-mp="bksf0la1xali">Policy Violation</option>
                  <option value="position-elimination" data-id="element-160" data-mp="geeu0k3otqkh">
                    Position Elimination
                  </option>
                  <option value="retirement" data-id="element-161" data-mp="2k40zqknra8m">Retirement</option>
                  <option value="end-of-contract" data-id="element-162" data-mp="7mcvird6ikcu">End of Contract</option>
                  <option value="other" data-id="element-163" data-mp="3osww856y6bw6">Other</option>
                </select>
                <p className="text-xs text-gray-500" data-id="element-164" data-mp="3tsi6vcvhc8oq">
                  This will be recorded in the user's employment history and
                  audit log.
                </p>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6" data-id="element-165" data-mp="vz6m89jx4erk">
              <div className="flex items-center mb-4" data-id="element-166" data-mp="r8889gmejsrh">
                <AlertTriangleIcon className="w-6 h-6 text-amber-600 mr-2" data-id="element-167" data-mp="r6x1d7xnu4w5" />
                <h3 className="text-base font-bold text-amber-900" data-id="element-168" data-mp="m9wwh0cjr86m">
                  Execution Summary
                </h3>
              </div>
              <ul className="space-y-3 text-sm text-amber-800" data-id="element-169" data-mp="h7xrxn52m583">
                <li className="flex items-start" data-id="element-170" data-mp="d7dzgwvfzpsa">
                  <CheckIcon className="w-5 h-5 mr-2 flex-shrink-0" data-id="element-171" data-mp="89ce25ajnedn" />
                  <span data-id="element-172" data-mp="89m5zyf5fvlz">
                    Freeze user login and deactivate Salesforce account
                  </span>
                </li>
                <li className="flex items-start" data-id="element-173" data-mp="22n4wioaridxs">
                  <CheckIcon className="w-5 h-5 mr-2 flex-shrink-0" data-id="element-174" data-mp="22mv8v7d6wiv9" />
                  <span data-id="element-175" data-mp="1xo0qg6j4wwbo">
                    Remove {config.removePermissions.length} permission sets and{' '}
                    {config.revokeLicenses.length} package licenses
                  </span>
                </li>
                {config.requiresRoundRobinDeactivation && <li className="flex items-start" data-id="element-176" data-mp="2ex3044mc1npo">
                    <CheckIcon className="w-5 h-5 mr-2 flex-shrink-0" data-id="element-177" data-mp="2eyebapi3xxqt" />
                    <span data-id="element-178" data-mp="2jve8sc0w5s6b">
                      Remove from active Round Robin distribution pools
                    </span>
                  </li>}
                {subordinates.length > 0 && <li className="flex items-start" data-id="element-179" data-mp="1r8k0sel9gl9q">
                    <CheckIcon className="w-5 h-5 mr-2 flex-shrink-0" data-id="element-180" data-mp="1w7elxn6palx0" />
                    <span data-id="element-181" data-mp="1w9hk4ui0blct">
                      Reassign {subordinates.length} subordinates to new
                      managers
                    </span>
                  </li>}
                {config.createRoundRobinOnExit && <li className="flex items-start" data-id="element-182" data-mp="3ckbs8igu9v0n">
                    <CheckIcon className="w-5 h-5 mr-2 flex-shrink-0" data-id="element-183" data-mp="3cn7lvulnlxja" />
                    <span className="font-bold" data-id="element-184" data-mp="1chajjb50fen7">
                      Execute Processor Special Logic: Create Round Robin entry
                      for active files
                    </span>
                  </li>}
              </ul>
            </div>
          </div>;
      case 4:
        // Execute
        return <div className="flex-1 flex flex-col items-center justify-center p-10 text-center min-h-[500px]" data-id="element-185" data-mp="tsdy7wrlbwh4">
            {isProcessing ? <div className="flex flex-col items-center" data-id="element-186" data-mp="ouue4g87oph9">
                <Loader2Icon className="w-16 h-16 text-navy-600 animate-spin mb-6" data-id="element-187" data-mp="12kpguwggqfmj" />
                <h2 className="text-xl font-bold text-gray-900 mb-2" data-id="element-188" data-mp="xu92990wonyl">
                  Executing Offboarding...
                </h2>
                <p className="text-sm text-gray-500" data-id="element-189" data-mp="a3ldzd6onlpv">
                  Processing metadata rules and transferring records.
                </p>
              </div> : <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} className="flex flex-col items-center" data-id="element-190" data-mp="3s3z4qq65dvw2">
                <div className="w-20 h-20 bg-[#2E844A] rounded-full flex items-center justify-center shadow-lg mb-6" data-id="element-191" data-mp="3n4mio7n3osuj">
                  <CheckIcon className="w-10 h-10 text-white" data-id="element-192" data-mp="eqxf108nbqpw" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-193" data-mp="joyyvndwbu2f">
                  Offboarding Complete
                </h2>
                <p className="text-sm text-gray-600 mb-8 max-w-md" data-id="element-194" data-mp="4qwvpeas1alp">
                  User has been successfully deactivated according to the{' '}
                  <span className="font-semibold" data-id="element-195" data-mp="2phxcdlld7u2v">{config?.title}</span>{' '}
                  metadata configuration.
                </p>

                <div className="grid grid-cols-2 gap-4 mb-10 w-full max-w-lg text-left" data-id="element-196" data-mp="2uhjplv171abo">
                  <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm" data-id="element-197" data-mp="2br93w6o08m8a">
                    <div className="text-2xl font-bold text-gray-900" data-id="element-198" data-mp="26q35bs7ny4qu">
                      {config?.removePermissions.length || 0}
                    </div>
                    <div className="text-xs font-medium text-gray-500 uppercase" data-id="element-199" data-mp="1xqg5haul16zw">
                      Permissions Removed
                    </div>
                  </div>
                  <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm" data-id="element-200" data-mp="2ufxlojr6ybfu">
                    <div className="text-2xl font-bold text-gray-900" data-id="element-201" data-mp="2pie4baz75ijo">
                      {config?.revokeLicenses.length || 0}
                    </div>
                    <div className="text-xs font-medium text-gray-500 uppercase" data-id="element-202" data-mp="2gq3u3toaxd2f">
                      Licenses Revoked
                    </div>
                  </div>
                  <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm" data-id="element-203" data-mp="38gi2jadjpx6p">
                    <div className="text-2xl font-bold text-gray-900" data-id="element-204" data-mp="38enevoaw3qyx">
                      {subordinates.length}
                    </div>
                    <div className="text-xs font-medium text-gray-500 uppercase" data-id="element-205" data-mp="zw5goazwmlh4">
                      Subordinates Reassigned
                    </div>
                  </div>
                  <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm" data-id="element-206" data-mp="18xddspqopg9m">
                    <div className="text-2xl font-bold text-green-600" data-id="element-207" data-mp="1dwyaetidtcj6">
                      Success
                    </div>
                    <div className="text-xs font-medium text-gray-500 uppercase" data-id="element-208" data-mp="1iurffj7y80hv">
                      Ownership Transferred
                    </div>
                  </div>
                </div>

                <button onClick={resetFlow} className="px-6 py-2.5 bg-white border border-gray-300 text-sm text-gray-700 font-medium rounded-full shadow-sm hover:bg-gray-50 transition-colors" data-id="element-209" data-mp="1rocxbbfetxvi">
                  Start New Offboarding
                </button>
              </motion.div>}
          </div>;
      default:
        return null;
    }
  };
  // Determine if we can advance to the next step
  const canAdvance = useMemo(() => {
    if (currentStep === 0) return selectedUserId !== null;
    if (currentStep === 1) return true;
    if (currentStep === 2) {
      return subordinates.every(sub => reassignments[sub.id]);
    }
    if (currentStep === 3) {
      const baseValid = transferLeads !== '' && transferCases !== '' && separationReason !== '';
      if (isLoanOfficerType) {
        return baseValid && transferLoanOfficer !== '' && transferLOA !== '' && transferLOA2 !== '';
      }
      if (isProcessorType) {
        return baseValid && transferLoanProcessor !== '' && transferProcessor2 !== '';
      }
      return baseValid;
    }
    return false;
  }, [currentStep, selectedUserId, subordinates, reassignments, transferLeads, transferLoanOfficer, transferLOA, transferLOA2, transferLoanProcessor, transferProcessor2, transferCases, separationReason, isLoanOfficerType, isProcessorType]);
  return <div className="flex flex-col h-full relative" data-id="element-210" data-mp="1r5aun64qgnco">
      {/* Header */}
      <div className="p-6 border-b border-gray-100 bg-white sticky top-0 z-10" data-id="element-211" data-mp="1m8kdsgl3tnq7">
        <div className="flex items-center justify-between" data-id="element-212" data-mp="1h97ir8k2xacj">
          <div data-id="element-213" data-mp="1h6lb78x6uuub">
            <h1 className="text-xl font-bold text-gray-900" data-id="element-214" data-mp="1c9l5iz493u3q">
              Offboarding Engine
            </h1>
            <p className="text-sm text-gray-500" data-id="element-215" data-mp="187q4zwu59kr0">
              Metadata-driven user termination and reassignment.
            </p>
          </div>
          {selectedUser && currentStep > 0 && currentStep < 4 && <div className="flex items-center bg-gray-50 rounded-full px-4 py-1.5 border border-gray-200" data-id="element-216" data-mp="32mkazjskez9r">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold mr-2 ${getAvatarColor(selectedUser.firstName)}`} data-id="element-217" data-mp="2xmo9psb95lxw">
                {selectedUser.firstName[0]}
                {selectedUser.lastName[0]}
              </div>
              <span className="text-sm font-medium text-gray-700" data-id="element-218" data-mp="37rnv12x73yyu">
                {selectedUser.firstName} {selectedUser.lastName}
              </span>
            </div>}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6 pb-24" data-id="element-219" data-mp="4antkkxsumk2">
        <AnimatePresence mode="wait" data-id="element-220" data-mp="8c9d2p4u9z38">
          <motion.div key={currentStep} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.2
        }} className="h-full" data-id="element-221" data-mp="8b7oriytbg3k">
            {renderStepContent()}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Footer Navigation */}
      {currentStep < 4 && <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-200 bg-white flex items-center justify-between" data-id="element-222" data-mp="27jhd3yaa5hu8">
          {currentStep > 0 ? <button onClick={handleBack} className="px-6 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors" data-id="element-223" data-mp="2gjcw2pa2uh4t">
              Back
            </button> : <div data-id="element-224" data-mp="1oszr0cjydkhu"></div>}

          {currentStep === 3 ? <button onClick={executeOffboarding} disabled={!canAdvance || isProcessing} className={`px-8 py-2.5 rounded-full font-medium text-sm transition-all shadow-sm ${!canAdvance || isProcessing ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-red-600 text-white hover:bg-red-700'}`} data-id="element-225" data-mp="22jldeb8m8ckh">
              Execute Termination
            </button> : <button onClick={handleNext} disabled={!canAdvance} className={`px-8 py-2.5 rounded-full font-medium text-sm transition-colors duration-150 ease-out shadow-sm ${!canAdvance ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-navy-600 text-white hover:bg-navy-700'}`} data-id="element-226" data-mp="1irpmse92k2y8">
              Next Step
            </button>}
        </div>}
    </div>;
}